import { useCallback, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useEmoji } from '../context/EmojiContext';
import { AddIcon, DeleteIcon, EditIcon, EyeOffIcon, EyeOnIcon, ImportIcon, RefreshIcon, SearchIcon } from './Icons';
import MetricCard from './MetricCard';
import PageBreadcrumb from './PageBreadcrumb';
import './DataPage.css';

const PAGE_SIZE = 10;

const tableCustomStyles = {
  table: {
    style: {
      backgroundColor: 'transparent',
    },
  },
  headRow: {
    style: {
      backgroundColor: 'var(--color-surface)',
      borderBottom: '1px solid var(--color-border)',
      minHeight: '48px',
    },
  },
  headCells: {
    style: {
      color: 'var(--color-text-muted)',
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
  },
  rows: {
    style: {
      backgroundColor: 'transparent',
      borderBottom: '1px solid var(--color-border)',
      minHeight: '48px',
    },
    highlightOnHoverStyle: {
      backgroundColor: 'var(--color-accent-soft)',
    },
  },
  cells: {
    style: {
      color: 'var(--color-text)',
      fontSize: '0.88rem',
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
  },
  pagination: {
    style: {
      backgroundColor: 'transparent',
      color: 'var(--color-text-muted)',
      borderTop: '1px solid var(--color-border)',
      fontSize: '0.82rem',
      minHeight: '52px',
    },
  },
  noData: {
    style: {
      backgroundColor: 'transparent',
      color: 'var(--color-text-muted)',
    },
  },
  progress: {
    style: {
      backgroundColor: 'transparent',
      color: 'var(--color-text-muted)',
    },
  },
};

function RowActions({ row, onEdit, onDelete, actionLoadingId }) {
  const { isEmojified, emojify } = useEmoji();
  const isLoading = actionLoadingId === row.id;

  return (
    <div className="data-page__actions">
      {onEdit && (
        <button
          type="button"
          className="data-page__action-btn data-page__action-btn--edit"
          onClick={() => onEdit(row)}
          disabled={isLoading}
          aria-label={emojify('✏️', 'Edit')}
          title={emojify('✏️', 'Edit')}
        >
          {isEmojified ? <span aria-hidden="true">✏️</span> : <EditIcon />}
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          className="data-page__action-btn data-page__action-btn--delete"
          onClick={() => onDelete(row)}
          disabled={isLoading}
          aria-label={emojify('🗑️', 'Delete')}
          title={emojify('🗑️', 'Delete')}
        >
          {isEmojified ? <span aria-hidden="true">🗑️</span> : <DeleteIcon />}
        </button>
      )}
    </div>
  );
}

function ConfirmModal({ title, message, detail, confirmLabel, loading, onClose, onConfirm }) {
  const { emojify } = useEmoji();

  return (
    <div className="data-page__modal-backdrop" onClick={onClose}>
      <div
        className="data-page__modal data-page__modal--confirm"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <h3>{title}</h3>
        <p className="data-page__confirm-message">{message}</p>
        {detail && <p className="data-page__confirm-detail">{detail}</p>}
        <div className="data-page__modal-actions">
          <button type="button" className="data-page__modal-btn" onClick={onClose} disabled={loading}>
            {emojify('✖️', 'Cancel')}
          </button>
          <button
            type="button"
            className="data-page__modal-btn data-page__modal-btn--danger"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? emojify('🗑️', 'Deleting...') : emojify('🗑️', confirmLabel)}
          </button>
        </div>
      </div>
    </div>
  );
}

function RowFormModal({
  title,
  submitLabel,
  savingLabel,
  fields,
  initialValues,
  saving,
  onClose,
  onSave,
  isSubmitEnabled,
}) {
  const { emojify } = useEmoji();
  const [values, setValues] = useState(() =>
    fields.reduce((acc, field) => {
      acc[field.key] = initialValues?.[field.key] ?? field.defaultValue ?? '';
      return acc;
    }, {})
  );
  const [fieldErrors, setFieldErrors] = useState({});
  const [visiblePasswords, setVisiblePasswords] = useState({});

  const canSubmit = isSubmitEnabled ? isSubmitEnabled(values, initialValues || {}) : true;

  const handleFieldChange = (key, value) => {
    setValues((current) => ({
      ...current,
      [key]: value,
    }));
    setFieldErrors((current) => {
      if (!current[key]) {
        return current;
      }
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const togglePasswordVisibility = (key) => {
    setVisiblePasswords((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canSubmit) {
      return;
    }

    const nextErrors = {};
    fields.forEach((field) => {
      const value = String(values[field.key] ?? '').trim();
      const isRequired = field.required !== false;
      const needsEmailValidation = field.validate === 'email' || field.type === 'email';

      if (isRequired && !value) {
        nextErrors[field.key] = needsEmailValidation
          ? 'A valid email address is required.'
          : `${field.label} is required.`;
        return;
      }

      if (needsEmailValidation && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        nextErrors[field.key] = 'A valid email address is required.';
      }
    });

    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    void onSave(values);
  };

  return (
    <div className="data-page__modal-backdrop" onClick={onClose}>
      <div
        className="data-page__modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <h3>{title}</h3>
        <form onSubmit={handleSubmit} noValidate>
          {fields.map((field) => (
            <label key={field.key} className="data-page__modal-field">
              <span>{field.label}</span>
              {field.type === 'select' ? (
                <select
                  value={values[field.key]}
                  onChange={(event) => handleFieldChange(field.key, event.target.value)}
                  required={field.required !== false}
                  aria-invalid={Boolean(fieldErrors[field.key])}
                >
                  {(field.placeholder || field.required !== false) && (
                    <option value="" disabled={field.required !== false}>
                      {field.placeholder || `Select ${field.label}`}
                    </option>
                  )}
                  {(field.options || []).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'password' ? (
                <div className="data-page__password-control">
                  <input
                    type={visiblePasswords[field.key] ? 'text' : 'password'}
                    value={values[field.key]}
                    onChange={(event) => handleFieldChange(field.key, event.target.value)}
                    required={field.required !== false}
                    placeholder={field.placeholder}
                    aria-invalid={Boolean(fieldErrors[field.key])}
                    autoComplete="new-password"
                    disabled={saving}
                  />
                  <button
                    type="button"
                    className="data-page__toggle-password"
                    onClick={() => togglePasswordVisibility(field.key)}
                    aria-label={
                      visiblePasswords[field.key] ? 'Hide password' : 'Show password'
                    }
                    disabled={saving}
                  >
                    {visiblePasswords[field.key] ? <EyeOnIcon /> : <EyeOffIcon />}
                  </button>
                </div>
              ) : (
                <input
                  type={field.type || 'text'}
                  value={values[field.key]}
                  onChange={(event) => handleFieldChange(field.key, event.target.value)}
                  required={field.required !== false}
                  placeholder={field.placeholder}
                  aria-invalid={Boolean(fieldErrors[field.key])}
                />
              )}
              {fieldErrors[field.key] && (
                <p className="data-page__field-error">{fieldErrors[field.key]}</p>
              )}
            </label>
          ))}
          <div className="data-page__modal-actions">
            <button type="button" className="data-page__modal-btn" onClick={onClose} disabled={saving}>
              {emojify('✖️', 'Cancel')}
            </button>
            <button
              type="submit"
              className="data-page__modal-btn data-page__modal-btn--primary"
              disabled={saving || !canSubmit}
            >
              {saving
                ? emojify('💾', savingLabel)
                : emojify(
                    submitLabel === 'Save' ? '💾' : submitLabel === 'Add' ? '➕' : '🚀',
                    submitLabel
                  )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditRowModal({ row, fields, saving, savingLabel, onClose, onSave, isSubmitEnabled }) {
  const { emojify } = useEmoji();
  const initialValues = fields.reduce((acc, field) => {
    const value = row[field.key] ?? '';
    acc[field.key] = field.maskedValue && value === field.maskedValue ? '' : value;
    return acc;
  }, {});

  return (
    <RowFormModal
      title={emojify('✏️', 'Edit')}
      submitLabel="Save"
      savingLabel={savingLabel}
      fields={fields}
      initialValues={initialValues}
      saving={saving}
      onClose={onClose}
      onSave={onSave}
      isSubmitEnabled={isSubmitEnabled}
    />
  );
}

function mapColumns(columns, { onEdit, onDelete, actionLoadingId }) {
  const dataColumns = columns.map((column) => {
    const isIdColumn = column.key === 'id';
    const cellClassName = [
      column.className,
      isIdColumn ? 'data-page__cell-id' : null,
    ]
      .filter(Boolean)
      .join(' ');

    const rdtColumn = {
      name: column.label,
      selector: (row) => row[column.key],
      sortable: true,
      cell: (row) => {
        const content = column.render ? column.render(row) : row[column.key] ?? '—';

        if (cellClassName) {
          return <div className={cellClassName}>{content}</div>;
        }

        return content;
      },
    };

    if (column.width) {
      rdtColumn.width = column.width;
    }

    if (column.minWidth) {
      rdtColumn.minWidth = column.minWidth;
    }

    if (column.maxWidth) {
      rdtColumn.maxWidth = column.maxWidth;
    }

    if (column.grow !== undefined) {
      rdtColumn.grow = column.grow;
    }

    if (isIdColumn) {
      rdtColumn.width = column.width ?? '72px';
      rdtColumn.minWidth = column.minWidth ?? '72px';
      rdtColumn.maxWidth = column.maxWidth ?? '88px';
      rdtColumn.grow = column.grow ?? 0;
    }

    return rdtColumn;
  });

  if (!onEdit && !onDelete) {
    return dataColumns;
  }

  return [
    ...dataColumns,
    {
      name: 'Actions',
      cell: (row) => (
        <RowActions
          row={row}
          onEdit={onEdit}
          onDelete={onDelete}
          actionLoadingId={actionLoadingId}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '88px',
    },
  ];
}

function DataPageLayout({
  title,
  subtitle,
  metrics,
  loading,
  importLabel,
  onImportClick,
  importLoading = false,
  onRefresh,
  onRowClick,
  columns,
  rows,
  rowKey = 'id',
  emptyMessage = 'No records found.',
  editFields,
  onSaveEdit,
  isEditSubmitEnabled,
  addLabel,
  addFields,
  onSaveAdd,
  isAddSubmitEnabled,
  addSavingLabel = 'Adding...',
  editSavingLabel = 'Saving...',
  onDelete,
  onDeleteMany,
  onDeleteAll,
  deleteConfirmMessage = 'Delete this record?',
  deleteManyConfirmMessage = 'Delete selected records?',
  deleteAllConfirmMessage = 'Delete all records?',
  searchable = false,
  searchPlaceholder = 'Search...',
  searchKeys,
  serverPagination = false,
  totalRows = 0,
  page = 1,
  rowsPerPage = PAGE_SIZE,
  onPageChange,
  onRowsPerPageChange,
}) {
  const { isEmojified, emojify } = useEmoji();
  const [searchQuery, setSearchQuery] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleClear, setToggleClear] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const showBulkActions = Boolean(onDeleteMany || onDeleteAll || importLabel || onRefresh || onSaveAdd);
  const showTableToolbar = showBulkActions || searchable;

  const filteredRows = useMemo(() => {
    if (!searchable) {
      return rows;
    }

    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return rows;
    }

    const keys = searchKeys?.length ? searchKeys : columns.map((column) => column.key);
    const tokens = query.split(/\s+/).filter(Boolean);

    return rows.filter((row) => {
      const haystack = keys
        .map((key) => String(row[key] ?? ''))
        .join(' ')
        .toLowerCase();

      return tokens.every((token) => haystack.includes(token));
    });
  }, [columns, rows, searchable, searchKeys, searchQuery]);

  const tableEmptyMessage = useMemo(() => {
    if (searchable && searchQuery.trim() && rows.length > 0 && filteredRows.length === 0) {
      return 'No matching records.';
    }

    return emptyMessage;
  }, [emptyMessage, filteredRows.length, rows.length, searchable, searchQuery]);

  const openConfirmDialog = useCallback(({ title, message, detail, confirmLabel, onConfirm }) => {
    setConfirmDialog({ title, message, detail, confirmLabel, onConfirm });
  }, []);

  const closeConfirmDialog = useCallback(() => {
    if (!confirmLoading) {
      setConfirmDialog(null);
    }
  }, [confirmLoading]);

  const handleConfirmDialog = useCallback(async () => {
    if (!confirmDialog?.onConfirm) {
      return;
    }

    setConfirmLoading(true);
    try {
      await confirmDialog.onConfirm();
      setConfirmDialog(null);
    } finally {
      setConfirmLoading(false);
    }
  }, [confirmDialog]);

  const handleEdit = useCallback(
    (row) => {
      if (editFields?.length && onSaveEdit) {
        setEditingRow(row);
      }
    },
    [editFields, onSaveEdit]
  );

  const handleSaveEdit = async (values) => {
    if (!editingRow || !onSaveEdit) {
      return;
    }

    setSaving(true);
    try {
      await onSaveEdit(editingRow, values);
      setEditingRow(null);
    } catch {
      // Page handlers surface toast/error; keep the modal open.
    } finally {
      setSaving(false);
    }
  };

  const addFormFields = addFields ?? editFields;

  const handleSaveAdd = async (values) => {
    if (!onSaveAdd) {
      return;
    }

    setAddSaving(true);
    try {
      await onSaveAdd(values);
      setShowAddModal(false);
    } catch {
      // Page handlers surface toast/error; keep the modal open.
    } finally {
      setAddSaving(false);
    }
  };

  const handleDelete = useCallback(
    (row) => {
      if (!onDelete) {
        return;
      }

      openConfirmDialog({
        title: 'Delete?',
        message: deleteConfirmMessage,
        confirmLabel: 'Delete',
        onConfirm: async () => {
          setActionLoadingId(row[rowKey]);
          try {
            await onDelete(row);
          } catch {
            // Page handlers surface toast/error.
          } finally {
            setActionLoadingId(null);
          }
        },
      });
    },
    [onDelete, deleteConfirmMessage, rowKey, openConfirmDialog]
  );

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedRows([]);
    setToggleClear((current) => !current);
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (!onDeleteMany || selectedRows.length === 0) {
      return;
    }

    openConfirmDialog({
      title: 'Delete?',
      message: deleteManyConfirmMessage,
      detail: `${selectedRows.length} record(s) selected`,
      confirmLabel: 'Delete Selected',
      onConfirm: async () => {
        setBulkDeleting(true);
        try {
          await onDeleteMany(selectedRows);
          exitSelectionMode();
        } catch {
          // Page handlers surface toast/error; stay in selection mode.
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  }, [
    onDeleteMany,
    selectedRows,
    deleteManyConfirmMessage,
    exitSelectionMode,
    openConfirmDialog,
  ]);

  const handleDeleteAll = useCallback(() => {
    if (!onDeleteAll || rows.length === 0) {
      return;
    }

    openConfirmDialog({
      title: 'Delete all?',
      message: deleteAllConfirmMessage,
      detail: `${rows.length} record(s) will be deleted`,
      confirmLabel: 'Delete All',
      onConfirm: async () => {
        setBulkDeleting(true);
        try {
          await onDeleteAll(rows);
          exitSelectionMode();
        } catch {
          // Page handlers surface toast/error.
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  }, [onDeleteAll, rows, deleteAllConfirmMessage, exitSelectionMode, openConfirmDialog]);

  const handleSelectedRowsChange = useCallback(({ selectedRows: nextSelectedRows }) => {
    setSelectedRows(nextSelectedRows);
  }, []);

  const handleRefresh = useCallback(async () => {
    if (!onRefresh) {
      return;
    }

    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh]);

  const showEdit = Boolean(editFields?.length && onSaveEdit);
  const tableColumns = useMemo(
    () =>
      mapColumns(columns, {
        onEdit: showEdit ? handleEdit : null,
        onDelete: onDelete ? handleDelete : null,
        actionLoadingId,
      }),
    [columns, showEdit, onDelete, handleEdit, handleDelete, actionLoadingId]
  );

  const paginationOptions = useMemo(
    () => ({
      rangeSeparatorText: 'of',
      rowsPerPageText: '',
    }),
    []
  );

  const handleRowClick = useCallback(
    (row) => {
      onRowClick?.(row);
    },
    [onRowClick]
  );

  return (
    <>
      <section className="data-page__header">
        <div className="data-page__intro">
          <PageBreadcrumb current={title} />
          <h2>{title}</h2>
          <p className="data-page__subtitle">{subtitle}</p>
        </div>
      </section>

      <section className="dashboard__metrics" aria-label="Summary">
        {metrics.map((metric, index) => (
          <MetricCard key={`${metric.title}-${index}`} {...metric} />
        ))}
      </section>

      <section className="data-page__table-section" aria-label={`${title} table`}>
        {showTableToolbar && (
          <div className="data-page__table-toolbar">
            {showBulkActions && (
              <div className="data-page__bulk-actions">
                {selectionMode ? (
                  <>
                    <button
                      type="button"
                      className="data-page__bulk-btn data-page__bulk-btn--delete"
                      onClick={handleDeleteSelected}
                      disabled={loading || bulkDeleting || selectedRows.length === 0}
                    >
                      {emojify('🗑️', `Delete Selected (${selectedRows.length})`)}
                    </button>
                    <button
                      type="button"
                      className="data-page__bulk-btn"
                      onClick={exitSelectionMode}
                      disabled={bulkDeleting}
                    >
                      {emojify('✖️', 'Cancel')}
                    </button>
                  </>
                ) : (
                  <>
                    {onRefresh && (
                      <button
                        type="button"
                        className="data-page__bulk-btn data-page__bulk-btn--refresh"
                        onClick={handleRefresh}
                        disabled={loading || refreshing || bulkDeleting || importLoading}
                      >
                        {refreshing ? (
                          <span className="data-page__spinner" aria-hidden="true" />
                        ) : (
                          !isEmojified && <RefreshIcon />
                        )}
                        {refreshing ? emojify('🔄', 'Refreshing...') : emojify('🔄', 'Refresh')}
                      </button>
                    )}
                    {importLabel && (
                      <button
                        type="button"
                        className="data-page__bulk-btn data-page__bulk-btn--import"
                        onClick={onImportClick}
                        disabled={importLoading || loading || bulkDeleting}
                      >
                        {!isEmojified && <ImportIcon />}
                        {importLoading && <span className="data-page__spinner" aria-hidden="true" />}
                        {importLoading ? emojify('📥', 'Importing...') : emojify('📥', importLabel)}
                      </button>
                    )}
                    {onSaveAdd && addLabel && (
                      <button
                        type="button"
                        className="data-page__bulk-btn data-page__bulk-btn--add"
                        onClick={() => setShowAddModal(true)}
                        disabled={loading || bulkDeleting || addSaving || importLoading}
                      >
                        {!isEmojified && <AddIcon />}
                        {emojify('➕', addLabel)}
                      </button>
                    )}
                    {onDeleteMany && (
                      <button
                        type="button"
                        className="data-page__bulk-btn"
                        onClick={() => setSelectionMode(true)}
                        disabled={loading || bulkDeleting || rows.length === 0}
                      >
                        {!isEmojified && <DeleteIcon />}
                        {emojify('🗑️', 'Delete Multiple')}
                      </button>
                    )}
                    {onDeleteAll && (
                      <button
                        type="button"
                        className="data-page__bulk-btn data-page__bulk-btn--delete"
                        onClick={handleDeleteAll}
                        disabled={loading || bulkDeleting || rows.length === 0}
                      >
                        {!isEmojified && <DeleteIcon />}
                        {emojify('🗑️', 'Delete All')}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            {searchable && (
              <label className="data-page__search">
                <span className="data-page__search-icon" aria-hidden="true">
                  {isEmojified ? <span aria-hidden="true">🔎</span> : <SearchIcon />}
                </span>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setToggleClear((current) => !current);
                  }}
                  placeholder={searchPlaceholder}
                  aria-label={emojify('🔎', searchPlaceholder)}
                  disabled={loading}
                />
              </label>
            )}
          </div>
        )}

        <div className="data-page__table-wrap data-page__rdt">
          <DataTable
            columns={tableColumns}
            data={filteredRows}
            keyField={rowKey}
            progressPending={loading}
            pagination
            paginationServer={serverPagination}
            paginationTotalRows={serverPagination ? totalRows : undefined}
            paginationDefaultPage={serverPagination ? page : 1}
            paginationPerPage={serverPagination ? rowsPerPage : PAGE_SIZE}
            paginationRowsPerPageOptions={[10, 25, 50]}
            paginationComponentOptions={paginationOptions}
            paginationResetDefaultPage={serverPagination ? false : toggleClear}
            onChangePage={serverPagination ? onPageChange : undefined}
            onChangeRowsPerPage={
              serverPagination
                ? (nextLimit) => onRowsPerPageChange?.(nextLimit)
                : undefined
            }
            customStyles={tableCustomStyles}
            noDataComponent={
              <div className="data-page__empty">{loading ? 'Loading...' : tableEmptyMessage}</div>
            }
            persistTableHead
            highlightOnHover
            responsive
            pointerOnHover={Boolean(onRowClick)}
            onRowClicked={onRowClick ? handleRowClick : undefined}
            selectableRows={selectionMode && Boolean(onDeleteMany)}
            onSelectedRowsChange={handleSelectedRowsChange}
            clearSelectedRows={toggleClear}
          />
        </div>
      </section>

      {editingRow && (
        <EditRowModal
          row={editingRow}
          fields={editFields}
          saving={saving}
          savingLabel={editSavingLabel}
          onClose={() => setEditingRow(null)}
          onSave={handleSaveEdit}
          isSubmitEnabled={isEditSubmitEnabled}
        />
      )}

      {showAddModal && addFormFields?.length && (
        <RowFormModal
          title={addLabel}
          submitLabel="Add"
          savingLabel={addSavingLabel}
          fields={addFormFields}
          saving={addSaving}
          onClose={() => !addSaving && setShowAddModal(false)}
          onSave={handleSaveAdd}
          isSubmitEnabled={isAddSubmitEnabled}
        />
      )}

      {confirmDialog && (
        <ConfirmModal
          title={confirmDialog.title}
          message={confirmDialog.message}
          detail={confirmDialog.detail}
          confirmLabel={confirmDialog.confirmLabel}
          loading={confirmLoading}
          onClose={closeConfirmDialog}
          onConfirm={handleConfirmDialog}
        />
      )}
    </>
  );
}

export default DataPageLayout;
